import t, { strLocaleCompare } from '@rcm/i18n-client';
import { CartErrorData } from 'cart-core';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useCalculateQuery, useFindShipMethodRulesQuery } from '../api';
import { selectDiscount } from '../slices/discount';
import { selectSkusSelectedQuantities } from '../slices/skusSelected';
import { selectView } from '../slices/view';
import { ViewID } from '../types';
import useProductQueries from './useProductQueries';
import useShipping from './useShipping';

const useResultsQueries = () => {
    const discountPercent = useSelector(selectDiscount);
    const skus = useSelector(selectSkusSelectedQuantities);
    const shipping = useShipping();
    const view = useSelector(selectView);

    const skusLength = useMemo(() => Object.keys(skus).length, [skus]);

    const shipMethodRules = useFindShipMethodRulesQuery(
        useMemo(
            () => ({
                discountPercent,
                shipping,
                skus,
            }),
            [discountPercent, shipping, skus]
        ),
        { skip: (
            view !== ViewID.RESULTS ||
            skusLength === 0
         ) }
    );

    const calculateQuery = useCalculateQuery(
        useMemo(
            () => ({
                discountPercent,
                skus,
                shipping,
                shipVia: shipMethodRules.data?.defaultMethod?.via
            }),
            [
                discountPercent,
                shipping,
                skus,
                shipMethodRules.data?.defaultMethod?.via,
            ]
        ),
        { skip: (
            !shipMethodRules.isSuccess ||
            view !== ViewID.RESULTS ||
            skusLength === 0
        ) }
    );

    const productsQueries = useProductQueries();

    const isError = (
        shipMethodRules.isError ||
        calculateQuery.isError ||
        skusLength === 0 ||
        productsQueries.isError
    );

    const isFetching = (
        shipMethodRules.isFetching ||
        calculateQuery.isFetching ||
        productsQueries.isFetching
    );

    const isLoading = (
        shipMethodRules.isLoading ||
        calculateQuery.isLoading ||
        productsQueries.isLoading
    );

    const isSuccess = (
        shipMethodRules.isSuccess &&
        calculateQuery.isSuccess &&
        productsQueries.isSuccess
    );

    const data = useMemo(
        () => {
            if (isFetching || isError) {
                return null;
            }
            const skuNames = Object.fromEntries(
                productsQueries?.data
                    ?.map((product) => product.skus)
                    .reduce((flattened, skus) => ([...flattened, ...skus]), [])
                    .map((sku) => ([sku.number, sku.name]))
            );
            const skuLines = Object.entries(skus)
                .map(([skuNumber, qty]) => (
                    [skuNames[skuNumber], qty] as [string, number]
                )).sort(([a], [b]) => strLocaleCompare(a, b));
            return (
                isSuccess ? {
                    ...calculateQuery.data,
                    skuLines,
                } : null
            );
        },
        [
            calculateQuery.data,
            isError,
            isFetching,
            isSuccess,
            productsQueries.data,
        ]
    );

    const error = useMemo(
        () => (
            skusLength == 0 ? {
                data: {
                    messages: [
                        {
                            value: t('Please add items before calculating totals'),
                            primary: true
                        }
                    ]
                }
            } :
            calculateQuery.error ? {
                data: {
                    // @ts-ignore
                    messages:
                        ((calculateQuery.error as any)
                            ?.data as CartErrorData)
                                ?.messages
                }
            } : null
        ),
        [calculateQuery.error, skusLength]
    );

    return {
        data,
        error,
        isError,
        isFetching,
        isLoading,
        isSuccess,
    };
}

export default useResultsQueries;
